/**
 * globalToast: 어디서든 호출 가능한 전역 알림/토스트 API
 *
 * Redux store를 직접 import해서 dispatch하므로
 * React 컴포넌트 바깥(유틸, 이벤트핸들러 등)에서도 사용 가능.
 *
 * 사용법:
 *   import { showNotification, showToast, dismissNotification } from '@/utils/globalToast';
 *
 *   // 상단 알림 (커스텀 버튼)
 *   const id = showNotification({
 *     text: '님이 참석을 요청하였습니다.',
 *     highlight: '홍길동',
 *     buttons: [
 *       { label: '거절', onClick: () => handleDeny() },
 *       { label: '승인', variant: 'blue', onClick: () => handleAllow() },
 *     ],
 *   });
 *
 *   // 우하단 채팅 토스트
 *   showToast({ title: '홍길동', message: '안녕하세요!' });
 *
 *   // 수동 닫기
 *   dismissNotification(id);
 */
import { store } from '@/store';
import { toastActions } from '@/store/slices/toastSlice';

// ─── 콜백 레지스트리 (직렬화 불가하므로 Redux 외부 관리) ──────────────────

const callbackRegistry = new Map<string, () => void>();

let idCounter = 0;

function generateId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${idCounter}_${Date.now()}`;
}

// ─── 상단 알림 ────────────────────────────────────────────────────────────

export interface NotificationButtonOption {
  label: string;
  variant?: 'default' | 'blue' | 'blue2';
  onClick: () => void;
}

export interface ShowNotificationOptions {
  /** 커스텀 알림 ID. 미지정 시 자동 생성. 동일 ID 중복 방지/외부 dismiss에 활용 */
  id?: string;
  /** 메인 텍스트 */
  text: string;
  /** <em> 태그로 강조할 텍스트 (text 앞에 표시) */
  highlight?: string;
  /** 버튼 배열 */
  buttons: NotificationButtonOption[];
  /** 자동 닫힘 시간(ms). 0이면 수동 닫기. 기본 0 */
  duration?: number;
  /** 버튼 클릭 시 자동으로 알림을 닫을지 여부. 기본 true */
  autoDismissOnClick?: boolean;
}

/**
 * 상단 알림을 표시한다.
 * @returns 알림 ID (수동 닫기용)
 */
export function showNotification(options: ShowNotificationOptions): string {
  const id = options.id || generateId('notif');

  // 동일 ID 알림이 이미 있으면 중복 방지
  const existing = store.getState().toast.notifications.find((n) => n.id === id);
  if (existing) return id;

  const autoDismiss = options.autoDismissOnClick !== false;

  const buttons = options.buttons.map((btn, idx) => {
    const btnId = `${id}_btn_${idx}`;
    callbackRegistry.set(btnId, () => {
      btn.onClick();
      if (autoDismiss) {
        dismissNotification(id);
      }
    });
    return { id: btnId, label: btn.label, variant: btn.variant };
  });

  store.dispatch(
    toastActions.addNotification({
      id,
      text: options.text,
      highlight: options.highlight,
      buttons,
      duration: options.duration ?? 0,
    }),
  );

  // 자동 닫힘
  if (options.duration && options.duration > 0) {
    setTimeout(() => dismissNotification(id), options.duration);
  }

  return id;
}

/**
 * 상단 알림을 닫는다.
 */
export function dismissNotification(id: string): void {
  // 관련 콜백 정리
  const state = store.getState().toast;
  const notification = state.notifications.find((n) => n.id === id);
  if (notification) {
    for (const btn of notification.buttons) {
      callbackRegistry.delete(btn.id);
    }
  }
  store.dispatch(toastActions.removeNotification(id));
}

/**
 * 모든 상단 알림을 닫는다.
 */
export function clearNotifications(): void {
  const state = store.getState().toast;
  for (const n of state.notifications) {
    for (const btn of n.buttons) {
      callbackRegistry.delete(btn.id);
    }
  }
  store.dispatch(toastActions.clearNotifications());
}

// ─── 우하단 채팅 토스트 ────────────────────────────────────────────────────

export interface ShowToastOptions {
  /** 토스트 제목 (발신자 이름 등) */
  title: string;
  /** 토스트 본문 */
  message: string;
  /** 자동 닫힘 시간(ms). 기본 4000 */
  duration?: number;
}

/**
 * 우하단 채팅형 토스트를 표시한다.
 * @returns 토스트 ID
 */
export function showToast(options: ShowToastOptions): string {
  const id = generateId('toast');
  const duration = options.duration ?? 4000;

  store.dispatch(
    toastActions.addToast({
      id,
      title: options.title,
      message: options.message,
      duration,
    }),
  );

  return id;
}

/**
 * 특정 토스트를 수동으로 닫는다.
 */
export function dismissToast(id: string): void {
  store.dispatch(toastActions.removeToast(id));
}

/**
 * 모든 토스트를 닫는다.
 */
export function clearToasts(): void {
  store.dispatch(toastActions.clearToasts());
}

// ─── 콜백 실행 (컴포넌트에서 호출) ─────────────────────────────────────────

/**
 * 버튼 콜백을 실행한다. GlobalNotification 컴포넌트에서 사용.
 */
export function executeButtonCallback(buttonId: string): void {
  const callback = callbackRegistry.get(buttonId);
  callback?.();
}
